/**
 * Consulta publica de CEP: ViaCEP (https://viacep.com.br) e, de reserva, a
 * BrasilAPI (https://brasilapi.com.br), que junta varias fontes.
 *
 * Mora aqui pelo mesmo motivo que `api.ts` e `contachatApi.ts`: quem abre
 * `src/services/` pra saber com quem o app conversa tem que ver todos os
 * destinos. Sao os unicos servicos de TERCEIRO que o front chama com host
 * fixo — os outros `fetch` do repo apontam pra URL dinamica (presigned do S3,
 * PDF, logo), entao nao aparecem numa busca por host.
 *
 * Sem variavel de ambiente de proposito: servicos publicos, sem chave e sem
 * ambiente de homologacao. Uma `REACT_APP_..._URL` so' criaria uma maneira
 * nova de quebrar.
 *
 * Reserva (02/10/2026): o ViaCEP oscila — no dia, 6 de 18 consultas ficaram
 * 30 s sem resposta, e a tela esperava sem limite. Agora ele tem 5 s; falhou,
 * estourou ou nao conhece o CEP, a BrasilAPI responde.
 *
 * O que NAO esta aqui: as regras de escrita no formulario (quais campos
 * sobrescrever, os toasts). Isso e' do `EnderecoForm`.
 */

export const VIACEP_URL = 'https://viacep.com.br/ws';
export const BRASILAPI_CEP_URL = 'https://brasilapi.com.br/api/cep/v2';
export const LIMITE_VIACEP_MS = 5000;
export const LIMITE_BRASILAPI_MS = 8000;

/** Endereco do CEP. CEP generico de cidade volta sem `logradouro` e `bairro`. */
export interface IEnderecoDoCep {
  logradouro?: string;
  bairro?: string;
  cidade?: string;
  uf?: string;
}

/** ViaCEP: CEP inexistente volta 200 com `erro` (booleano ou o texto "true"). */
interface IRespostaViaCep {
  logradouro?: string;
  bairro?: string;
  localidade?: string;
  uf?: string;
  erro?: boolean | string;
}

/** BrasilAPI v2: CEP inexistente volta 404. */
interface IRespostaBrasilApi {
  street?: string | null;
  neighborhood?: string | null;
  city?: string | null;
  state?: string | null;
}

// O limite cobre a resposta inteira, corpo incluido: servidor que manda o
// cabecalho e cala tambem e' cortado.
const pedir = async (url: string, limiteMs: number): Promise<{ status: number; dados: any }> => {
  const controle = new AbortController();
  const relogio = setTimeout(() => controle.abort(), limiteMs);
  try {
    const res = await fetch(url, { signal: controle.signal });
    return { status: res.status, dados: res.ok ? await res.json() : null };
  } finally {
    clearTimeout(relogio);
  }
};

/** `undefined` = nao conhece o CEP. Erro de rede, de HTTP ou de tempo: lanca. */
const noViaCep = async (cep: string): Promise<IEnderecoDoCep | undefined> => {
  const { status, dados } = await pedir(`${VIACEP_URL}/${cep}/json/`, LIMITE_VIACEP_MS);
  if (status !== 200) throw new Error(`ViaCEP ${status}`);
  const resposta = dados as IRespostaViaCep;
  if (resposta.erro) return undefined;
  return {
    logradouro: resposta.logradouro,
    bairro: resposta.bairro,
    cidade: resposta.localidade,
    uf: resposta.uf,
  };
};

/** `undefined` = nao conhece o CEP. Erro de rede, de HTTP ou de tempo: lanca. */
const naBrasilApi = async (cep: string): Promise<IEnderecoDoCep | undefined> => {
  const { status, dados } = await pedir(`${BRASILAPI_CEP_URL}/${cep}`, LIMITE_BRASILAPI_MS);
  if (status === 404) return undefined;
  if (status !== 200) throw new Error(`BrasilAPI ${status}`);
  const resposta = dados as IRespostaBrasilApi;
  return {
    logradouro: resposta.street ?? undefined,
    bairro: resposta.neighborhood ?? undefined,
    cidade: resposta.city ?? undefined,
    uf: resposta.state ?? undefined,
  };
};

/**
 * Endereco do CEP (8 digitos, so' numeros). `null` = o CEP nao existe: uma
 * fonte disse isso e a outra nao achou nem respondeu. Lanca so' quando as duas
 * falham.
 */
export const consultarCep = async (cep: string): Promise<IEnderecoDoCep | null> => {
  let viaCepNaoConhece = false;
  try {
    const endereco = await noViaCep(cep);
    if (endereco) return endereco;
    viaCepNaoConhece = true;
  } catch {
    // ViaCEP fora do ar ou lento: vai para a reserva.
  }
  try {
    return (await naBrasilApi(cep)) ?? null;
  } catch (erro) {
    if (viaCepNaoConhece) return null;
    throw erro;
  }
};
